<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import WhatsAppCampaignDetails from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDetails.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const reportRefreshTimer = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const selectedDetailsCampaign = computed(() => {
  const campaignId = Number(route.params.campaignId);
  if (!campaignId) return null;

  return (
    WhatsAppCampaigns.value.find(campaign => campaign.id === campaignId) || null
  );
});

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleDetails = campaign => {
  router.push({
    name: 'campaigns_whatsapp_show',
    params: { ...route.params, campaignId: campaign.id },
  });
};

const handleBackToCampaigns = () => {
  router.push({
    name: 'campaigns_whatsapp_index',
    params: { accountId: route.params.accountId },
  });
};

const refreshCampaigns = async () => {
  await store.dispatch('campaigns/get');
};

const stopReportRefresh = () => {
  if (!reportRefreshTimer.value) return;

  clearInterval(reportRefreshTimer.value);
  reportRefreshTimer.value = null;
};

const startReportRefresh = () => {
  stopReportRefresh();
  reportRefreshTimer.value = setInterval(() => {
    if (!isFetchingCampaigns.value) refreshCampaigns();
  }, 15000);
};

watch(
  selectedDetailsCampaign,
  campaign => {
    if (campaign) {
      startReportRefresh();
      return;
    }

    stopReportRefresh();
  },
  { immediate: true }
);

onBeforeUnmount(stopReportRefresh);

const openWhatsAppCampaignDialog = () => {
  toggleWhatsAppCampaignDialog(true);
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    :close-on-outside="false"
    @click="openWhatsAppCampaignDialog"
    @close="toggleWhatsAppCampaignDialog(false)"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <WhatsAppCampaignDetails
      v-else-if="selectedDetailsCampaign"
      :campaign="selectedDetailsCampaign"
      :is-refreshing="isFetchingCampaigns"
      @back="handleBackToCampaigns"
      @refresh="refreshCampaigns"
    />
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      show-details-action
      @delete="handleDelete"
      @details="handleDetails"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
